/**
 * Holds: the dated exemption from inactivity reclaim — docs/infra/bundle-contract.md § Holds, limits
 * from ADR-0024 § Exemptions. Pure, so the routes and the owner page apply the same rules.
 */

import { bundleLifecyclePath, isIsoTimestamp, withBundleId } from "./bundle";

/** ADR-0024's hold limits. Server-enforced; the UI only mirrors them. */
export const BUNDLE_HOLD_LIMITS = {
	adminMaxDays: 180,
	selfMaxDays: 30,
	/** Self-service holds after the first one in a chain. */
	selfMaxRenewals: 2,
	/** A hold ending within this many days is flagged so it does not lapse into a surprise. */
	expiringSoonDays: 7,
	reasonMaxLength: 500,
} as const;

/** Who placed a hold: an admin for anyone's bundle, or the owner for their own. */
export type BundleHoldKind = "admin" | "self";

/** The body of a hold placement. Both fields are required: a hold is always dated and explained. */
export type BundleHoldRequest = { holdUntil: string; reason: string };

/** One entry of the append-only hold log. `renewal` counts self-service holds after the first. */
export type BundleHoldLogEntry = {
	bundleType: string;
	slug: string;
	/** Absent = the only bundle of its type (bundle-contract.md § Outputs). */
	bundleId?: string;
	action: "place" | "clear";
	kind: BundleHoldKind;
	/** ISO-8601. Absent on a clear. */
	holdUntil?: string;
	reason?: string;
	/** Catalog ref of the caller. */
	setBy: string;
	renewal: number;
	/** ISO-8601. When the self-service chain this entry belongs to began. */
	chainStartedAt: string;
	recordedAt: string;
};

/** The hold on a bundle now, as the owner page reads it. */
export type BundleHold = {
	holdUntil: string;
	reason?: string;
	setBy?: string;
	kind: BundleHoldKind;
};

/** Why an owner cannot place a self-service hold now. */
export type BundleSelfHoldRefusal = "admin-hold" | "renewals-used";

/** Response body of `GET /lifecycle/:bundleType/mine/hold`. */
export type MyBundleHoldResponse = {
	/** Absent = no row of this type for the caller. */
	slug?: string;
	/** The active hold. Absent = none. */
	hold?: BundleHold;
	/** Self-service holds the caller may still place in the current chain, this one included. */
	selfHoldsLeft: number;
	/** ISO-8601. The latest date a self-service hold placed now may run to. */
	selfMaxUntil: string;
	/** Set when a self-service hold would be refused now. */
	refused?: BundleSelfHoldRefusal;
};

/** Response body of the admin hold routes: the bundle's active hold after the change. */
export type BundleHoldResponse = { hold?: BundleHold };

const DAY_MS = 86_400_000;

/** Whether a hold ending at `holdUntil` still exempts the bundle at `now`. */
export function isHoldActive(holdUntil: string | undefined, now: Date): boolean {
	return Boolean(holdUntil) && Date.parse(holdUntil!) > now.getTime();
}

/** The latest end date a hold of `kind` placed at `now` may have. */
export function holdMaxUntil(kind: BundleHoldKind, now: Date): Date {
	const days =
		kind === "admin" ? BUNDLE_HOLD_LIMITS.adminMaxDays : BUNDLE_HOLD_LIMITS.selfMaxDays;
	return new Date(now.getTime() + days * DAY_MS);
}

/** Every way a hold placement of `kind` at `now` breaks the rules. Empty = valid. */
export function bundleHoldRequestErrors(value: unknown, kind: BundleHoldKind, now: Date): string[] {
	if (typeof value !== "object" || value === null) return ["body must be an object"];
	const r = value as Record<string, unknown>;
	const errors: string[] = [];
	if (!isIsoTimestamp(r.holdUntil)) {
		errors.push("holdUntil is required and must be ISO-8601: a hold is always dated");
	} else if (Date.parse(r.holdUntil) <= now.getTime()) {
		errors.push("holdUntil must be in the future");
	} else if (Date.parse(r.holdUntil) > holdMaxUntil(kind, now).getTime()) {
		const max =
			kind === "admin" ? BUNDLE_HOLD_LIMITS.adminMaxDays : BUNDLE_HOLD_LIMITS.selfMaxDays;
		errors.push(
			`holdUntil is beyond the ${max}-day limit for ${kind === "admin" ? "an admin" : "a self-service"} hold`,
		);
	}
	const reason = typeof r.reason === "string" ? r.reason.trim() : "";
	if (!reason) errors.push("reason is required");
	else if (reason.length > BUNDLE_HOLD_LIMITS.reasonMaxLength) {
		errors.push(`reason must be at most ${BUNDLE_HOLD_LIMITS.reasonMaxLength} characters`);
	}
	return errors;
}

/**
 * A self-service hold's place in its renewal chain, or why it is refused. The chain survives a clear or
 * a lapse; only use or an admin entry ends it (bundle-contract.md § Holds).
 */
export function nextSelfHold(input: {
	latest?: Pick<
		BundleHoldLogEntry,
		"action" | "kind" | "holdUntil" | "renewal" | "chainStartedAt"
	>;
	lastSeenAt?: string;
	now: Date;
}): { renewal: number; chainStartedAt: string } | { refused: BundleSelfHoldRefusal } {
	const { latest, lastSeenAt, now } = input;
	if (
		latest?.action === "place" &&
		latest.kind === "admin" &&
		isHoldActive(latest.holdUntil, now)
	) {
		return { refused: "admin-hold" };
	}
	const usedSince =
		latest &&
		lastSeenAt !== undefined &&
		Date.parse(lastSeenAt) > Date.parse(latest.chainStartedAt);
	if (!latest || latest.kind !== "self" || usedSince) {
		return { renewal: 0, chainStartedAt: now.toISOString() };
	}
	const renewal = latest.renewal + 1;
	if (renewal > BUNDLE_HOLD_LIMITS.selfMaxRenewals) return { refused: "renewals-used" };
	return { renewal, chainStartedAt: latest.chainStartedAt };
}

const holdPath = (bundleType: string, slugOrMine: string) =>
	`${bundleLifecyclePath(bundleType)}/${encodeURIComponent(slugOrMine)}/hold`;

/** Self-service hold path for the caller's own bundle of a type. */
export function bundleMyHoldPath(bundleType: string, bundleId?: string): string {
	return withBundleId(holdPath(bundleType, "mine"), bundleId);
}

/** Service-only: records that the expiry notice for the hold ending `holdUntil` went out. */
export function bundleHoldNoticePath(bundleType: string, slug: string, bundleId?: string): string {
	return withBundleId(`${holdPath(bundleType, slug)}/notice`, bundleId);
}

/** Body of the hold-notice PUT: the hold end the notice named. Recorded only if it is still the hold. */
export type BundleHoldNoticeRequest = { holdUntil: string };

/** Response of the hold-notice PUT. `recorded`: false when the hold changed since the evaluator read it. */
export type BundleHoldNoticeResponse = { recorded: boolean };

/** Admin hold path for one bundle. */
export function bundleHoldPath(bundleType: string, slug: string, bundleId?: string): string {
	return withBundleId(holdPath(bundleType, slug), bundleId);
}
